import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  Home, BookOpen, Calculator, FlaskConical, Gamepad2, Trophy,
  FileText, Star, LogOut, Flame, Menu, X, BookMarked
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export const StudentLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const navItems = [
    { path: '/student', label: 'Home', icon: Home },
    { path: '/student/reading', label: 'Reading', icon: BookMarked },
    { path: '/student/games', label: 'Games', icon: Gamepad2 },
    { path: '/student/achievements', label: 'Badges', icon: Trophy },
    { path: '/student/exams', label: 'Exams', icon: FileText },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FFFBEB' }}>
      {/* Top Nav */}
      <nav className="bg-white border-b-4 border-gray-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/student" className="flex items-center gap-2 font-heading font-bold text-xl text-purple-600">
              <img src="/logo.png" alt="Growing Minds" className="h-10 w-auto" />
            </Link>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-2">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`student-sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              ))}
            </div>

            {/* User Section */}
            <div className="flex items-center gap-4">
              {/* Streak */}
              <div className="hidden sm:flex items-center gap-1 bg-orange-100 px-3 py-1.5 rounded-full border-2 border-orange-300">
                <Flame className="w-4 h-4 text-orange-500" />
                <span className="font-bold text-orange-700">{user?.streak_days || 0}</span>
              </div>

              {/* Points */}
              <div className="hidden sm:flex items-center gap-1 bg-purple-100 px-3 py-1.5 rounded-full border-2 border-purple-300">
                <Star className="w-4 h-4 text-purple-500" />
                <span className="font-bold text-purple-700">{user?.total_points || 0}</span>
              </div>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-purple-200 rounded-full border-2 border-gray-800 flex items-center justify-center">
                    <span className="font-bold text-purple-700">
                      {user?.first_name?.charAt(0)}
                    </span>
                  </div>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem disabled className="font-semibold">
                    {user?.first_name} {user?.last_name}
                  </DropdownMenuItem>
                  <DropdownMenuItem disabled className="text-sm text-slate-500">
                    Year {user?.year_group}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer">
                    <LogOut className="w-4 h-4 mr-2" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Mobile Menu Button */}
              <button
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t border-gray-200">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`block px-4 py-3 rounded-xl font-semibold ${
                    isActive(item.path) ? 'bg-purple-100 text-purple-700' : 'text-slate-600'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon className="w-5 h-5" />
                    {item.label}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        {children}
      </main>
    </div>
  );
};

export default StudentLayout;
